// @givmo/passkeys — a removal signs the console out, however the person leaves its result (AS-BE63).
//
// A spent recovery code, or a finished reset that takes the authenticator off, removes a second
// factor, and the backend then ends every session of the account (`revoke_sessions`), the suite
// session included, as Identity Platform ends every Firebase one (a passkey-only reset ends none).
// From then on every read answers 401, so the result is the last thing this session can show.
// Whichever way the person leaves it — its Done, or the sheet's own dismissal (Escape, the backdrop,
// anything that unmounts it) — the console signs out through the host's own `signOut`, which also
// releases the suite session, exactly once; the person signs in again, without a code. Nothing is
// owed before the removal, nor by a host that hands no `signOut`.

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

/** The host's own sign-out (`useAuth().signOut`): it releases the suite session, then Firebase's. */
export type RemovalSignOut = () => Promise<void> | void;

/**
 * `owe()` once the removal has happened; `settle()` signs out if that is owed (Done calls it as it
 * closes). Unmounting settles too, so a dismissal signs out as Done does, and never twice.
 */
export function useSignOutAfterRemoval(signOut: RemovalSignOut | undefined) {
  // The host's latest sign-out: a re-render may hand a new one, and the unmount must use it.
  const latest = useRef(signOut);
  useLayoutEffect(() => {
    latest.current = signOut;
  }, [signOut]);
  const owed = useRef(false);
  const owe = useCallback(() => {
    if (latest.current !== undefined) owed.current = true;
  }, []);
  const settle = useCallback(() => {
    if (!owed.current) return;
    owed.current = false;
    void latest.current?.();
  }, []);
  // On unmount only. StrictMode's rehearsal unmount comes before anything can be owed.
  useEffect(() => settle, [settle]);
  return { owe, settle };
}
